import { PhoneNumber } from '@weave/schema-gen-ts/dist/phonenumbers/phonenumber.pb';
import { CallStatus } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { CallIntelConstant, CallIntelTypes } from '@frontend/api-call-intel';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { AI_OFFICE_USER_UUID } from '../constants';

dayjs.extend(isBetween);

export const callStatusUtils = {
  /**
   * Returns true if the call is still in processing state
   */
  isCallInProcessing(status?: CallStatus): boolean {
    return (
      status === CallStatus.CALL_STATUS_INFERENCING ||
      status === CallStatus.CALL_STATUS_PENDING ||
      status === CallStatus.CALL_STATUS_TRANSCRIBING
    );
  },

  /**
   * Returns true if the audio quality (MOS score) is considered poor (1–3.5)
   */
  isPoorAudio(call?: CallIntelTypes.Call): boolean {
    const mos = call?.mosScore;
    if (!mos) {
      // Older records might not have mosScore hence do not treat them as of bad quality
      return false;
    }
    return mos >= 1 && mos <= 3.5;
  },

  /**
   * Returns true if the call status indicates failure
   */
  isFailedCall(status?: CallStatus): boolean {
    return status === CallStatus.CALL_STATUS_FAILED || status === CallStatus.CALL_STATUS_ERROR;
  },

  /**
   * Returns true if the call status indicates it was skipped
   */
  isSkippedCall(status?: CallStatus): boolean {
    return status === CallStatus.CALL_STATUS_SKIPPED;
  },

  isPrivateCall(call?: CallIntelTypes.Call): boolean {
    return !!call?.isPrivate;
  },

  /**
   * Aggregates call status flags
   */
  getCallFlags(call?: CallIntelTypes.Call) {
    const status = call?.status;
    const skipped = this.isSkippedCall(status);
    const failed = this.isFailedCall(status);
    const processing = this.isCallInProcessing(status);
    const analyzed = !(skipped || failed || processing);
    const poorAudio = this.isPoorAudio(call);
    const isPrivateCall = this.isPrivateCall(call);

    return {
      isSkippedCall: skipped,
      isFailedCall: failed,
      isProcessingCall: processing,
      isAnalyzed: analyzed,
      isPoorAudio: poorAudio,
      isPrivateCall,
    };
  },
};

export const callIntelUtils = {
  /**
   * Returns the phone number as a string in the format: +<countryCode><nationalNumber>
   * @param phoneNumber The phone number object
   * @returns The formatted phone number string
   */
  getPhoneNumber(phoneNumber?: PhoneNumber): string {
    if (!phoneNumber) return '';
    const { countryCode, nationalNumber } = phoneNumber;
    return `${countryCode}${nationalNumber}`; // e.g. +14155552671
  },
  isOfficeUserDataUnavailable: (recordDate?: string): boolean => {
    // As confirmed by the BE, office user data is not available prior to 1 April 2024 (including this date)
    // Hence, hard coding the date to 2nd April 2024
    const unavailableOfficeUserDataDate = new Date(2024, 3, 2);
    return !!recordDate && dayjs(recordDate).isBefore(dayjs(unavailableOfficeUserDataDate));
  },
  isUserInactive: (_officeUser?: CallIntelTypes.OfficeUser | null): boolean => {
    // TODO :: add inactive user logic when we have one, for now return false for all users
    return false;
  },
  getFullName: (firstName?: string, lastName?: string, defaultValue = '') => {
    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
    return defaultValue || '';
  },
  getContactInfo: (call: CallIntelTypes.Call) => {
    const locationId = call?.locationId ?? '';
    const personId = call?.person?.id ?? '';
    const firstName = call?.person?.firstName ?? '';
    const lastName = call?.person?.lastName ?? '';
    const profileName = callIntelUtils.getFullName(firstName, lastName);

    const number = formatPhoneNumber(callIntelUtils.getPhoneNumber(call?.phoneNumber));
    return { firstName, lastName, locationId, number, personId, profileName };
  },
};

/**
 * Computes the difference between two objects and returns changed values. Used in call-analysis to detect form changes.
 */
export const diffValues = <T extends Record<string, any>>(initial: T, current: T): Partial<T> | null => {
  const diff = Object.keys(initial).reduce((acc, key) => {
    const initialValue = initial[key];
    const currentValue = current[key];
    if (currentValue === undefined) return acc;
    const areEqual = Array.isArray(initialValue)
      ? initialValue.length === currentValue.length && initialValue.every((value: any) => currentValue.includes(value))
      : initialValue === currentValue;
    if (!areEqual) {
      (acc as Record<string, any>)[key] = currentValue;
    }
    return acc;
  }, {} as Partial<T>);

  return Object.keys(diff).length > 0 ? diff : null;
};

export const getHighlightedCitation = (tasks: CallIntelTypes.Task[], highlightedTaskId?: string | null) => {
  if (!highlightedTaskId) return undefined;

  const task = tasks.find((task) => task.id === highlightedTaskId);

  if (task && task.citation && task.citationTime) {
    return { text: task.citation, time: task.citationTime };
  }

  return undefined;
};

/**
 * Sorts an array of string values based on a given priority map.
 * Items listed in `overrideFirst` are pulled out first (in the order they appear),
 * then the remaining items are sorted by their mapped priority (lower = higher priority).
 *
 * @param items - Array of items to sort
 * @param priorityMap - A map of item → numeric priority
 * @param overrideFirst - Items that should always appear first (in their original order)
 * @returns A new sorted array with `overrideFirst` items first, then the rest by priority
 */
export const sortWithPriorityMap = <T extends string>(
  items: T[],
  priorityMap: Partial<Record<T, number>>,
  overrideFirst: T[] = []
): T[] => {
  // take the items that are in the overrideFirst list (and preserve their order)
  const firstSet = overrideFirst.filter((i) => items.includes(i));

  const rest = items
    .filter((item) => !firstSet.includes(item))
    .sort((a, b) => {
      const priorityA = priorityMap[a] ?? Number.MAX_SAFE_INTEGER;
      const priorityB = priorityMap[b] ?? Number.MAX_SAFE_INTEGER;
      return priorityA - priorityB;
    });
  return [...firstSet, ...rest];
};

/**
 * Given a Call Intel time period label (e.g., "Today", "This Week"),
 * returns the corresponding TimePeriodType.
 * If no direct mapping exists, returns TIMEPERIOD_TYPE_CUSTOM.
 */
export const toTimePeriodType = (callIntelKey: string): CallIntelTypes.TimePeriodTypeEnum => {
  return (
    CallIntelConstant.CallIntelTimePeriodLabelToType[callIntelKey] ??
    CallIntelTypes.TimePeriodTypeEnum.TIMEPERIOD_TYPE_CUSTOM
  );
};

export const callIntelAiUtils = {
  isAiOfficeUser: (officeUser?: CallIntelTypes.OfficeUser | null): boolean => {
    return officeUser?.userId === AI_OFFICE_USER_UUID;
  },
  isValidAiOutcome: (aiOutcome?: AIOutcome): boolean => {
    return !!aiOutcome && aiOutcome !== AIOutcome.AI_OUTCOME_UNKNOWN;
  },
};

/**
 * Checks if any page in a call list contains private calls
 * @param pages - Array of pages from infinite query result
 * @returns true if at least one page has private calls, false otherwise
 */
export const checkForPrivateCallsInCallList = (
  pages?: {
    rows: CallIntelTypes.Call[];
    hasPrivateCalls?: boolean;
  }[]
): boolean => {
  if (!pages || pages.length === 0) return false;
  return pages.some((page) => page?.hasPrivateCalls === true);
};
